import React from 'react'
import clsx from 'clsx'
import {
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
} from '@material-ui/core'
import MuiListItem from '@material-ui/core/ListItem'
import { makeStyles, withStyles } from '@material-ui/core/styles'
import { NavLink } from 'react-router-dom'

const ICON_CLASS = 'AppShellSidebarItem-icon'

// withStyles (not styled) is required here: MUI v4's styled() intercepts
// the polymorphic `component` prop and renders it directly, bypassing
// MuiListItem entirely. withStyles preserves MuiListItem's own rendering,
// including ButtonBase composition when `button` is true.
const ExtendedListItem = withStyles(theme => ({
  root: {
    color: theme.palette.sidebar.itemText,
    borderRadius: theme.palette.sidebar.itemBorderRadius,
    marginLeft: 12,
    marginRight: 12,
    width: 'calc(100% - 24px)',
    paddingLeft: 16,
    paddingRight: 24,
    minHeight: 56,
    transition: theme.transitions.create(
      ['background-color', 'background-image'],
      { duration: theme.transitions.duration.shortest },
    ),
    '&:hover': {
      backgroundColor: theme.palette.sidebar.hoverOverlay,
    },
    // M3 focus state layer: 12% on-secondary-container.
    '&.Mui-focusVisible': {
      backgroundColor: theme.palette.sidebar.focusOverlay,
    },
    '&.Mui-selected': {
      backgroundColor: theme.palette.sidebar.activeBg,
      color: theme.palette.sidebar.itemText,
    },
    '&.Mui-selected:hover': {
      backgroundColor: theme.palette.sidebar.activeBg,
    },
    // When selected and keyboard-focused, layer the focus overlay on
    // top of the active background so the focus ring is still visible.
    '&.Mui-selected.Mui-focusVisible': {
      backgroundColor: theme.palette.sidebar.activeBg,
      backgroundImage: `linear-gradient(${theme.palette.sidebar.focusOverlay}, ${theme.palette.sidebar.focusOverlay})`,
    },
  },
}))(MuiListItem)

const useItemStyles = makeStyles({
  collapsedItem: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: 8,
    paddingBottom: 8,
    paddingLeft: 4,
    paddingRight: 4,
    marginLeft: 4,
    marginRight: 4,
    width: 'calc(100% - 8px)',
    minHeight: 48,
  },
  iconWrapper: {
    color: 'inherit',
    minWidth: 40,
    [`& .${ICON_CLASS}`]: {
      height: 24,
      width: 24,
    },
  },
  iconWrapperCollapsed: {
    minWidth: 0,
    justifyContent: 'center',
  },
})

export default function SidebarItem({
  icon,
  onClick,
  label,
  path,
  location,
  exact,
  annotation,
  tooltip,
  disabled = false,
  external = false,
  hide = false,
  collapsed = false,
  isSelected,
}) {
  const classes = useItemStyles()
  const iconComponent = React.cloneElement(icon, {
    className: ICON_CLASS,
  })

  const isBookingsPage =
    path.includes('bookings') &&
    location.pathname.includes('bookings') &&
    !location.pathname.includes('schedule') &&
    !path.includes('schedule') &&
    !location.pathname.includes('events')
  const isEventPage =
    path.includes('schedule') &&
    location.pathname.includes('events') &&
    !location.pathname.includes('offerings') &&
    !path.includes('offerings')

  const selected =
    typeof isSelected === 'function'
      ? isSelected({ path, location, exact })
      : exact
        ? `${path}/` === location.pathname ||
        path === location.pathname ||
        isBookingsPage ||
        isEventPage
        : location.pathname.startsWith(path)
  const linkProps = !external
    ? {
      component: NavLink,
      to: path,
      ...(typeof isSelected === 'function'
        ? {
          isActive: (match, location) => isSelected({ path, location, exact }),
        }
        : {}),
    }
    : { component: 'a', href: path, target: external.target || '_blank' }

  if (hide) return null

  const item = (
    <ExtendedListItem
      button
      onClick={onClick}
      selected={selected}
      disabled={disabled}
      aria-current={selected ? 'page' : undefined}
      className={clsx({ [classes.collapsedItem]: collapsed })}
      {...linkProps}
    >
      <ListItemIcon
        className={clsx(classes.iconWrapper, {
          [classes.iconWrapperCollapsed]: collapsed,
        })}
      >
        {iconComponent}
      </ListItemIcon>
      {!collapsed && (
        <ListItemText
          primary={label}
          primaryTypographyProps={{ style: { fontWeight: 500 } }}
        />
      )}
      {annotation && !collapsed && (
        <ListItemSecondaryAction>{annotation}</ListItemSecondaryAction>
      )}
    </ExtendedListItem>
  )

  if (collapsed) {
    return (
      <Tooltip
        title={tooltip || (typeof label === 'string' ? label : '')}
        placement="right"
        enterDelay={200}
      >
        {item}
      </Tooltip>
    )
  }

  return item
}
